import { useCallback, useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import { Alert, Layouts, Loader, NoData } from 'components'
import { head } from 'helpers'
import * as api from 'api'
import * as XLSX from 'xlsx'

function describeNonPdfResponseBody(u8) {
  const text = new TextDecoder('utf-8', { fatal: false }).decode(u8.slice(0, 4000))
  const trimmed = text.trim()
  if (!trimmed) return 'Empty response body (not a PDF).'
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const j = JSON.parse(trimmed)
      if (typeof j.message === 'string' && j.message) return j.message
      if (typeof j.error === 'string' && j.error) return j.error
    } catch {
      /* ignore */
    }
  }
  if (trimmed.startsWith('<!') || /<html[\s>]/i.test(trimmed)) {
    return 'Received HTML instead of a PDF. Check login, backend URL (REACT_APP_API_HOST should be like http://localhost:8080/api with no extra /api in paths), and try again.'
  }
  return `Response was not a PDF (starts with: ${trimmed.slice(0, 160).replace(/\s+/g, ' ')})`
}

/** Reads the response body and only downloads if bytes look like a real PDF. */
async function savePdfFromResponse(response, downloadName) {
  const ab = await response.arrayBuffer()
  const u8 = new Uint8Array(ab)
  const sig =
    u8.length >= 4
      ? String.fromCharCode(u8[0], u8[1], u8[2], u8[3])
      : ''
  if (sig !== '%PDF') {
    throw new Error(describeNonPdfResponseBody(u8))
  }
  const blob = new Blob([ab], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = downloadName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default function BusinessesMasterData() {
  const [loading, setLoading] = useState(false)
  const [businesses, setBusinesses] = useState([])
  const [exporting, setExporting] = useState(false)
  const [magazineExporting, setMagazineExporting] = useState(false)
  const [memberMagazineExporting, setMemberMagazineExporting] = useState(false)
  const [magCity, setMagCity] = useState('')
  const [magState, setMagState] = useState('')
  const [magLimit, setMagLimit] = useState('80')
  const [magMemberLimit, setMagMemberLimit] = useState('60')

  useEffect(() => {
    head({ title: 'Businesses Master Data | BCN Admin' })
    fetchBusinesses()
  }, [])

  const fetchBusinesses = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.admin.business.fetchAllWithUsers({})
      if (response?.code === 200) {
        setBusinesses(response.payload.businesses || [])
      } else if (response?.message) {
        Alert.error(response.message)
      }
    } catch (error) {
      console.error('Failed to load businesses:', error)
      Alert.error('Failed to load businesses data')
    } finally {
      setLoading(false)
    }
  }, [])

  const handleExportToExcel = async () => {
    if (!businesses || businesses.length === 0) {
      Alert.error('No data to export')
      return
    }

    setExporting(true)
    try {
      // Prepare data for Excel export
      const excelData = businesses.map((business, index) => ({
        'S.No.': index + 1,
        'Business Name': business.businessName,
        'Business Category': business.businessCategory,
        'Business Sub Categories': business.businessSubCategories,
        'Business Phone': business.businessPhone,
        'Business GST': business.businessGST,
        'Business Website': business.businessWebsite,
        'Business Address': business.businessAddress,
        'Business State': business.businessState,
        'Business City': business.businessCity,
        'Business Pin Code': business.businessPinCode,
        'Business Facebook Link': business.businessFacebookLink,
        'Business Instagram Link': business.businessInstagramLink,
        'Date of Establishment': business.businessDateOfEstablishment,
        'Business Tags': business.businessTags,
        'Business Views': business.businessViews,
        'Average Rating': business.businessAverageRating,
        'Total Reviews': business.businessTotalReviews,
        'Approval Status': business.businessApprovalStatus,
        'Is Approved': business.businessIsApproved,
        'Is Blocked': business.businessIsBlocked,
        'Engagement Type': business.businessEngagementType,
        'Business Created At': business.businessCreatedAt,
        'Business Updated At': business.businessUpdatedAt,
        'User Full Name': business.userFullName,
        'User First Name': business.userFirstName,
        'User Last Name': business.userLastName,
        'User Email': business.userEmail,
        'User Phone': business.userPhone,
        'User Address': business.userAddress,
        'User State': business.userState,
        'User City': business.userCity,
        'User Pin Code': business.userPinCode,
        'User Ref ID': business.userRefId,
        'User Plan': business.userPlan,
        'User Plan Expires At': business.userPlanExpiresAt,
        'User Created At': business.userCreatedAt
      }))

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData)
      
      // Set column widths
      const columnWidths = [
        { wch: 8 },   // S.No.
        { wch: 25 },  // Business Name
        { wch: 20 },  // Business Category
        { wch: 25 },  // Business Sub Categories
        { wch: 15 },  // Business Phone
        { wch: 15 },  // Business GST
        { wch: 25 },  // Business Website
        { wch: 30 },  // Business Address
        { wch: 15 },  // Business State
        { wch: 15 },  // Business City
        { wch: 12 },  // Business Pin Code
        { wch: 30 },  // Business Facebook Link
        { wch: 30 },  // Business Instagram Link
        { wch: 20 },  // Date of Establishment
        { wch: 20 },  // Business Tags
        { wch: 12 },  // Business Views
        { wch: 12 },  // Average Rating
        { wch: 12 },  // Total Reviews
        { wch: 15 },  // Approval Status
        { wch: 12 },  // Is Approved
        { wch: 12 },  // Is Blocked
        { wch: 15 },  // Engagement Type
        { wch: 20 },  // Business Created At
        { wch: 20 },  // Business Updated At
        { wch: 25 },  // User Full Name
        { wch: 20 },  // User First Name
        { wch: 20 },  // User Last Name
        { wch: 25 },  // User Email
        { wch: 15 },  // User Phone
        { wch: 30 },  // User Address
        { wch: 15 },  // User State
        { wch: 15 },  // User City
        { wch: 12 },  // User Pin Code
        { wch: 15 },  // User Ref ID
        { wch: 12 },  // User Plan
        { wch: 20 },  // User Plan Expires At
        { wch: 20 }   // User Created At
      ]
      worksheet['!cols'] = columnWidths

      // Create workbook
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Businesses Master Data')

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0]
      const filename = `Businesses_Master_Data_${date}.xlsx`

      // Write file
      XLSX.writeFile(workbook, filename)
      
      Alert.success('Business data exported to Excel successfully!')
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      Alert.error('Failed to export data to Excel')
    } finally {
      setExporting(false)
    }
  }

  const handleExportToPDF = async () => {
    if (!businesses || businesses.length === 0) {
      Alert.error('No data to export')
      return
    }

    setExporting(true)
    try {
      const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080/api'
      const authentication = window.localStorage.getItem('authentication')
      const BACKEND_ACCESS_KEY = process.env.REACT_APP_BACKEND_ACCESS_KEY

      // Call backend endpoint to generate PDF
      const response = await fetch(`${API_HOST}/admin/business/exportPDF`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'backendAccessKey': BACKEND_ACCESS_KEY || '',
          'authentication': authentication || ''
        },
        body: JSON.stringify({ businesses })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to generate PDF')
      }

      const date = new Date().toISOString().split('T')[0]
      await savePdfFromResponse(response, `Businesses_Master_Data_${date}.pdf`)

      Alert.success('Business data exported to PDF successfully!')
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      // Fallback: Generate PDF on frontend using window.print()
      try {
        await generatePDFFrontend()
      } catch (fallbackError) {
        console.error('Fallback PDF generation failed:', fallbackError)
        Alert.error('Failed to export data to PDF. Please try Excel export instead.')
      }
    } finally {
      setExporting(false)
    }
  }

  const handleExportDirectoryMagazine = async () => {
    if (magazineExporting) return
    setMagazineExporting(true)
    try {
      const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080/api'
      const authentication = window.localStorage.getItem('authentication')
      const BACKEND_ACCESS_KEY = process.env.REACT_APP_BACKEND_ACCESS_KEY

      const limit = Math.min(200, Math.max(1, parseInt(String(magLimit).replace(/\D/g, ''), 10) || 80))
      const payload = { limit }
      if (magState.trim()) payload.state = magState.trim()
      if (magCity.trim()) payload.city = magCity.trim()

      const response = await fetch(`${API_HOST}/admin/business/exportDirectoryMagazine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          backendAccessKey: BACKEND_ACCESS_KEY || '',
          authentication: authentication || ''
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}))
        throw new Error(errJson.message || `Export failed (${response.status})`)
      }

      const date = new Date().toISOString().split('T')[0]
      await savePdfFromResponse(response, `BCN_Business_Directory_${date}.pdf`)
      Alert.success('Business directory magazine PDF downloaded.')
    } catch (error) {
      console.error('Magazine PDF export failed:', error)
      Alert.error(error.message || 'Failed to generate directory magazine PDF')
    } finally {
      setMagazineExporting(false)
    }
  }

  const handleExportMemberBusinessMagazine = async () => {
    if (memberMagazineExporting) return
    setMemberMagazineExporting(true)
    try {
      const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080/api'
      const authentication = window.localStorage.getItem('authentication')
      const BACKEND_ACCESS_KEY = process.env.REACT_APP_BACKEND_ACCESS_KEY

      const limit = Math.min(200, Math.max(1, parseInt(String(magMemberLimit).replace(/\D/g, ''), 10) || 60))
      const payload = { limit }
      if (magState.trim()) payload.state = magState.trim()
      if (magCity.trim()) payload.city = magCity.trim()

      const response = await fetch(`${API_HOST}/admin/business/exportMemberBusinessMagazine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          backendAccessKey: BACKEND_ACCESS_KEY || '',
          authentication: authentication || ''
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}))
        throw new Error(errJson.message || `Export failed (${response.status})`)
      }

      const date = new Date().toISOString().split('T')[0]
      await savePdfFromResponse(response, `BCN_Members_Businesses_${date}.pdf`)
      Alert.success('Member + business magazine PDF downloaded.')
    } catch (error) {
      console.error('Member+business magazine export failed:', error)
      Alert.error(error.message || 'Failed to generate member + business magazine PDF')
    } finally {
      setMemberMagazineExporting(false)
    }
  }

  const generatePDFFrontend = async () => {
    // Create a temporary div with the data table
    const printContent = document.createElement('div')
    printContent.style.padding = '20px'
    printContent.style.fontFamily = 'Arial, sans-serif'
    
    let html = `
      <div style="text-align: center; margin-bottom: 20px;">
        <h1>Businesses Master Data</h1>
        <p>Generated on: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
        <p>Total Records: ${businesses.length}</p>
      </div>
      <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
        <thead>
          <tr style="background-color: #f0f0f0;">
            <th style="border: 1px solid #ddd; padding: 8px;">S.No.</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Business Name</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Category</th>
            <th style="border: 1px solid #ddd; padding: 8px;">User Name</th>
            <th style="border: 1px solid #ddd; padding: 8px;">User Phone</th>
            <th style="border: 1px solid #ddd; padding: 8px;">User Email</th>
            <th style="border: 1px solid #ddd; padding: 8px;">State</th>
            <th style="border: 1px solid #ddd; padding: 8px;">City</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Status</th>
          </tr>
        </thead>
        <tbody>
    `

    businesses.forEach((business, index) => {
      html += `
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px;">${index + 1}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.businessName || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.businessCategory || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.userFullName || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.userPhone || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.userEmail || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.businessState || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.businessCity || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${business.businessApprovalStatus || 'N/A'}</td>
        </tr>
      `
    })

    html += `
        </tbody>
      </table>
    `

    printContent.innerHTML = html
    document.body.appendChild(printContent)

    // Use window.print() as fallback
    window.print()
    
    // Remove the temporary element after a delay
    setTimeout(() => {
      document.body.removeChild(printContent)
    }, 1000)
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Businesses Master Data'>
        <div className={s.headerActions}>
          <div className={s.info}>
            <p>Total Businesses: <strong>{businesses.length}</strong></p>
          </div>
          <div className={s.exportButtons}>
            <div className={s.magazineFilters}>
              <label>
                State (optional)
                <input type='text' placeholder='Any' value={magState} onChange={e => setMagState(e.target.value)} />
              </label>
              <label>
                City (optional)
                <input type='text' placeholder='Any' value={magCity} onChange={e => setMagCity(e.target.value)} />
              </label>
              <label>
                Max businesses (directory)
                <input type='number' min={1} max={200} value={magLimit} onChange={e => setMagLimit(e.target.value)} />
              </label>
              <label>
                Max members (member+biz)
                <input type='number' min={1} max={200} value={magMemberLimit} onChange={e => setMagMemberLimit(e.target.value)} />
              </label>
            </div>
            <button
              type='button'
              onClick={handleExportDirectoryMagazine}
              disabled={magazineExporting || memberMagazineExporting || loading}
              className={s.exportButtonMagazine}
            >
              <span className='material-icons-outlined'>auto_stories</span>
              {magazineExporting ? 'Building PDF…' : 'Directory magazine (PDF)'}
            </button>
            <button
              type='button'
              onClick={handleExportMemberBusinessMagazine}
              disabled={memberMagazineExporting || magazineExporting || loading}
              className={s.exportButtonMagazine}
            >
              <span className='material-icons-outlined'>groups</span>
              {memberMagazineExporting ? 'Building PDF…' : 'Members + businesses (PDF)'}
            </button>
            <button 
              onClick={handleExportToExcel} 
              disabled={exporting || loading || businesses.length === 0}
              className={s.exportButton}
            >
              <span className='material-icons-outlined'>file_download</span>
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
            <button 
              onClick={handleExportToPDF} 
              disabled={exporting || loading || businesses.length === 0}
              className={s.exportButton}
            >
              <span className='material-icons-outlined'>picture_as_pdf</span>
              {exporting ? 'Exporting...' : 'Export to PDF'}
            </button>
          </div>
        </div>
      </Layouts.Classic>

      {loading ? (
        <div className={s.loader}>
          <Loader color='var(--c-primary)' colorText='var(--c-primary)' />
        </div>
      ) : businesses.length === 0 ? (
        <NoData message='No businesses found' />
      ) : (
        <div className={s.tableContainer}>
          <table className={s.dataTable}>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Business Name</th>
                <th>Category</th>
                <th>User Name</th>
                <th>User Phone</th>
                <th>User Email</th>
                <th>State</th>
                <th>City</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((business, index) => (
                <tr key={business.businessId || index}>
                  <td>{index + 1}</td>
                  <td>{business.businessName || 'N/A'}</td>
                  <td>{business.businessCategory || 'N/A'}</td>
                  <td>{business.userFullName || 'N/A'}</td>
                  <td>{business.userPhone || 'N/A'}</td>
                  <td>{business.userEmail || 'N/A'}</td>
                  <td>{business.businessState || 'N/A'}</td>
                  <td>{business.businessCity || 'N/A'}</td>
                  <td>
                    <span className={`${s.status} ${s[business.businessApprovalStatus?.toLowerCase()]}`}>
                      {business.businessApprovalStatus || 'N/A'}
                    </span>
                  </td>
                  <td>{business.businessCreatedAt || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

